import { css } from "@emotion/css";
import { Button, Drawer, Tooltip } from "@octopusdeploy/design-system-components";
import { StringHelper } from "@octopusdeploy/design-system-octopus-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { ArgoCDApplicationResource, ArgoCDApplicationSourceResource } from "@octopusdeploy/octopus-server-client";
import { Repository, isHelmApplicationSource, getStaticAndParentEnvironmentsDependingOnIfEphemeralEnvironmentsAreEnabled } from "@octopusdeploy/octopus-server-client";
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import type { ConfiguredSource, ConfiguredSources } from "~/areas/infrastructure/components/ArgoCDInstances/components/GenerateArgoCDAnnotationsDrawer/ConfiguredSource";
import { AnnotationsView } from "~/areas/infrastructure/components/ArgoCDInstances/components/GenerateArgoCDAnnotationsDrawer/components/AnnotationsView";
import { HelmExpressionConfigurationView } from "~/areas/infrastructure/components/ArgoCDInstances/components/GenerateArgoCDAnnotationsDrawer/components/HelmExpressionConfigurationView";
import { ScopingConfigurationView } from "~/areas/infrastructure/components/ArgoCDInstances/components/GenerateArgoCDAnnotationsDrawer/components/ScopingConfigurationView";
import { SourceSelectionTable } from "~/areas/infrastructure/components/ArgoCDInstances/components/GenerateArgoCDAnnotationsDrawer/components/SourceSelectionTable";
import { useEphemeralEnvironmentsEnabled } from "~/areas/projects/components/EphemeralEnvironments/useEphemeralEnvironmentsEnabled";
import Select from "~/primitiveComponents/form/Select/Select";
import { sharedStyles } from "./sharedStyles";
const styles = {
    mainContainer: css({
        display: "flex",
        flexDirection: "column",
        gap: space[24],
        padding: space[16],
        paddingBottom: space[24],
    }),
    contentContainer: css({
        display: "flex",
        flexDirection: "column",
        gap: space[16],
    }),
    selectContainer: css({
        width: "50%",
    }),
};
type CurrentMultiSourceTab = "Selection" | "ScopingConfiguration" | "HelmConfiguration" | "Copy";
type InnerActions = {
    reset: () => void;
};
type GenerateArgoCdAnnotationsDrawerInnerProps = {
    gatewayId?: string;
    initiallySelectedApplicationId?: string;
    initiallySelectedApplicationSourceIndex?: number;
    initiallySelectedProjectSlug?: string;
    currentTab: CurrentMultiSourceTab;
    onSelectedApplicationChanged: (app: ArgoCDApplicationResource) => void;
    onSelectedSourcesChanged: (selectedSources: ArgoCDApplicationSourceResource[]) => void;
    onConfiguredSourcesChanged: (configuredSources: ConfiguredSources) => void;
};
const appHasMultipleSources = (app: ArgoCDApplicationResource) => app.Sources.length > 1;
const appHasAnyHelmSources = (app: ArgoCDApplicationResource) => app.Sources.filter(isHelmApplicationSource).length > 0;
export const GenerateArgoCdAnnotationsDrawerInner = forwardRef<InnerActions, GenerateArgoCdAnnotationsDrawerInnerProps>((props, ref) => {
    const { currentTab, onSelectedApplicationChanged, onSelectedSourcesChanged, onConfiguredSourcesChanged, gatewayId, initiallySelectedProjectSlug, initiallySelectedApplicationId, initiallySelectedApplicationSourceIndex } = props;
    const ephemeralEnvironmentsEnabled = useEphemeralEnvironmentsEnabled();
    const [selectedApplication, setSelectedApplication] = useState<ArgoCDApplicationResource | undefined>(undefined);
    const [selectedSources, setSelectedSources] = useState<ArgoCDApplicationSourceResource[]>([]);
    const [configuredSources, setConfiguredSources] = useState<ConfiguredSources>({});
    const { result: applications, isLoading, error, } = useInlineStatusQuery(async (repo) => {
        if (gatewayId) {
            const response = await repo.ArgoCDGateways.listApps(gatewayId);
            return response.Resources;
        }
        else {
            const instances = await repo.ArgoCDInstances.list();
            const applicationsResponsePromises = instances.Resources.map((instance) => repo.ArgoCDGateways.listApps(instance.GatewayId, { take: Repository.takeAll }));
            const applicationResponses = await Promise.all(applicationsResponsePromises);
            return applicationResponses.map((ar) => ar.Resources).reduce((accumulator, value) => accumulator.concat(value), []);
        }
    }, [gatewayId], "Listing Argo CD applications");
    const { result: targetData, isLoading: isLoadingTargetData, error: targetDataError, } = useInlineStatusQuery(async (repo) => {
        const projectsPromise = repo.Projects.all();
        const environmentsPromise = getStaticAndParentEnvironmentsDependingOnIfEphemeralEnvironmentsAreEnabled(ephemeralEnvironmentsEnabled, repo);
        const tenantsPromise = repo.Tenants.all();
        return {
            projects: await projectsPromise,
            environments: await environmentsPromise,
            tenants: await tenantsPromise,
        };
    }, [ephemeralEnvironmentsEnabled], "List projects, environments and tenants for annotations drawer");
    const findApplication = useCallback((id: string | undefined) => applications?.find((app) => app.Id === id), [applications]);
    const isMultiSourceApplication = selectedApplication ? appHasMultipleSources(selectedApplication) : false;
    const hasAnyHelmSources = selectedApplication ? appHasAnyHelmSources(selectedApplication) : false;
    const items = useMemo(() => (applications || []).map((app) => ({
        value: app.Id,
        text: app.Name,
    })), [applications]);
    const updateConfiguredSources = useCallback((configured: ConfiguredSources) => {
        setConfiguredSources(configured);
        onConfiguredSourcesChanged(configured);
    }, [onConfiguredSourcesChanged]);
    const presetConfiguredSources = useCallback((selectedApplication: ArgoCDApplicationResource | undefined, selectedSources: ArgoCDApplicationSourceResource[]) => {
        const newConfiguredSources = (selectedApplication?.Sources || []).reduce<Record<number, ConfiguredSource>>((dict, src) => {
            dict[src.Index] = {
                source: src,
                isSelectedForScoping: selectedSources.some((selectedSrc) => selectedSrc.Index === src.Index),
                isHelmSource: isHelmApplicationSource(src),
                projectSlug: initiallySelectedProjectSlug,
            };
            return dict;
        }, {});
        updateConfiguredSources(newConfiguredSources);
    }, [initiallySelectedProjectSlug, updateConfiguredSources]);
    const updateSelectedSources = useCallback((selectedApplication: ArgoCDApplicationResource | undefined, selectedSources: ArgoCDApplicationSourceResource[]) => {
        setSelectedSources(selectedSources);
        onSelectedSourcesChanged(selectedSources);
        //preset the configured sources
        presetConfiguredSources(selectedApplication, selectedSources);
    }, [onSelectedSourcesChanged, presetConfiguredSources]);
    const updatedSelectedApplication = useCallback((id: string | undefined) => {
        const selectedApp = findApplication(id);
        setSelectedApplication(selectedApp);
        if (selectedApp) {
            onSelectedApplicationChanged(selectedApp);
        }
        //we've changed the application, so update the selected sources
        //if we have no application, or it's multiple sources, nothing is selected, otherwise, the single source is selected
        updateSelectedSources(selectedApp, !selectedApp || appHasMultipleSources(selectedApp) ? [] : selectedApp.Sources);
    }, [findApplication, onSelectedApplicationChanged, updateSelectedSources]);
    useEffect(() => {
        //if we don't have a selected application, and we have a response with an application, preselect it
        if (!selectedApplication) {
            if (initiallySelectedApplicationId) {
                updatedSelectedApplication(initiallySelectedApplicationId);
                const selectedApp = findApplication(initiallySelectedApplicationId);
                if (selectedApp && initiallySelectedApplicationSourceIndex && selectedSources.length <= 0) {
                    updateSelectedSources(selectedApp, selectedApp.Sources?.filter((s) => s.Index === initiallySelectedApplicationSourceIndex) ?? []);
                }
            }
            else if (applications?.[0]) {
                updatedSelectedApplication(applications[0].Id);
            }
        }
    }, [applications, findApplication, initiallySelectedApplicationId, initiallySelectedApplicationSourceIndex, selectedApplication, selectedSources.length, updateSelectedSources, updatedSelectedApplication]);
    useImperativeHandle(ref, () => ({
        reset: () => {
            //set the selected application to undefined, which then should force the re-selection of the first item
            setSelectedApplication(undefined);
        },
    }));
    const content = useMemo(() => {
        const applicationSelection = (<div>
                <h2 className={sharedStyles.header}>1. Select an application</h2>
                <div className={styles.contentContainer}>
                    <div className={styles.selectContainer}>
                        <Select items={items} value={selectedApplication?.Id} onChange={updatedSelectedApplication} label={"Application"} allowClear={false} allowFilter={true} disabled={isLoading}/>
                    </div>
                </div>
            </div>);
        if (isMultiSourceApplication) {
            switch (currentTab) {
                case "Selection":
                    return (<>
                            {applicationSelection}
                            <SourceSelectionTable application={selectedApplication} selectedSources={selectedSources} setSelectedSources={(srcs) => updateSelectedSources(selectedApplication, srcs)}/>
                        </>);
                case "ScopingConfiguration":
                    return (<ScopingConfigurationView index={3} configuredSources={configuredSources} setConfiguredSources={updateConfiguredSources} projects={targetData?.projects} environments={targetData?.environments} tenants={targetData?.tenants} isMultiSourceApplication={isMultiSourceApplication}/>);
                case "HelmConfiguration":
                    return <HelmExpressionConfigurationView index={4} configuredSources={configuredSources} setConfiguredSources={updateConfiguredSources} isMultiSourceApplication={isMultiSourceApplication}/>;
                case "Copy":
                    return <AnnotationsView index={4} configuredSources={configuredSources} isMultiSourceApplication={isMultiSourceApplication}/>;
            }
        }
        else {
            return (<>
                    {applicationSelection}
                    <ScopingConfigurationView index={2} configuredSources={configuredSources} setConfiguredSources={updateConfiguredSources} projects={targetData?.projects} environments={targetData?.environments} tenants={targetData?.tenants} isMultiSourceApplication={isMultiSourceApplication}/>
                    {hasAnyHelmSources && <HelmExpressionConfigurationView index={3} configuredSources={configuredSources} setConfiguredSources={updateConfiguredSources} isMultiSourceApplication={isMultiSourceApplication}/>}
                    <AnnotationsView index={3} configuredSources={configuredSources} isMultiSourceApplication={isMultiSourceApplication}/>
                </>);
        }
    }, [
        configuredSources,
        currentTab,
        hasAnyHelmSources,
        isLoading,
        isMultiSourceApplication,
        items,
        selectedApplication,
        selectedSources,
        targetData?.environments,
        targetData?.projects,
        targetData?.tenants,
        updateConfiguredSources,
        updateSelectedSources,
        updatedSelectedApplication,
    ]);
    return <div className={styles.mainContainer}>{content}</div>;
});
type GenerateArgoCDAnnotationsDrawerProps = Pick<GenerateArgoCdAnnotationsDrawerInnerProps, "gatewayId" | "initiallySelectedApplicationId" | "initiallySelectedApplicationSourceIndex" | "initiallySelectedProjectSlug"> & {
    open: boolean;
    onClose: () => void;
};
export const GenerateArgoCDAnnotationsDrawer = (props: GenerateArgoCDAnnotationsDrawerProps) => {
    const { open, onClose, ...rest } = props;
    const [currentTab, setCurrentTab] = useState<CurrentMultiSourceTab>("Selection");
    const [isMultiSourceApp, setIsMultiSourceApp] = useState<boolean>(false);
    const [hasAnyHelmSources, setHasAnyHelmSources] = useState<boolean>(false);
    const [hasAnySelectedSources, setHasAnySelectedSources] = useState<boolean>(false);
    const [hasValidSelectedSources, setHasValidSelectedSources] = useState<boolean>(false);
    const [allConfiguredSourcesValid, setAllConfiguredSourcesValid] = useState<boolean>(false);
    const innerRef = useRef<InnerActions>(null);
    const handleSelectedApplicationChanged = (app: ArgoCDApplicationResource) => {
        setIsMultiSourceApp(appHasMultipleSources(app));
        setHasAnyHelmSources(appHasAnyHelmSources(app));
    };
    const handleSelectedSourcesChanged = (selectedSources: ArgoCDApplicationSourceResource[]) => {
        setHasAnySelectedSources(selectedSources.length > 0);
        setHasValidSelectedSources(selectedSources.every((src) => !StringHelper.isNullOrWhiteSpace(src.Name)));
    };
    const handleConfiguredSourcesChanged = (configuredSources: ConfiguredSources) => {
        setAllConfiguredSourcesValid(Object.entries(configuredSources)
            .filter(([, configuredSource]) => configuredSource.isSelectedForScoping)
            .every(
        //selected configured sources require project & environment slugs.
        ([, configuredSource]) => !StringHelper.isNullOrWhiteSpace(configuredSource.projectSlug || null) && !StringHelper.isNullOrWhiteSpace(configuredSource.environmentSlug || null)));
    };
    const resetToStart = () => {
        setCurrentTab("Selection");
        innerRef.current?.reset();
    };
    const handleOnClose = useCallback(() => {
        onClose();
        setCurrentTab("Selection");
        setIsMultiSourceApp(false);
    }, [onClose]);
    const multiSourceTabSequence: CurrentMultiSourceTab[] = useMemo(() => (hasAnyHelmSources ? ["Selection", "ScopingConfiguration", "HelmConfiguration", "Copy"] : ["Selection", "ScopingConfiguration", "Copy"]), [hasAnyHelmSources]);
    const moveToNextTab = useCallback(() => {
        const idx = multiSourceTabSequence.indexOf(currentTab);
        if (idx === multiSourceTabSequence.length) {
            throw new Error("Cannot move past the end of the list of tabs");
        }
        setCurrentTab(multiSourceTabSequence[idx + 1]);
    }, [currentTab, multiSourceTabSequence]);
    const moveToPreviousTab = useCallback(() => {
        const idx = multiSourceTabSequence.indexOf(currentTab);
        if (idx === 0) {
            throw new Error("Cannot move past the start of the list of tabs");
        }
        setCurrentTab(multiSourceTabSequence[idx - 1]);
    }, [currentTab, multiSourceTabSequence]);
    const customActions = useMemo(() => {
        const closeButton = <Button importance="quiet" onClick={handleOnClose} label="Close"/>;
        const doneButton = <Button importance="primary" onClick={handleOnClose} label="Done"/>;
        if (isMultiSourceApp) {
            switch (currentTab) {
                case "Selection":
                    let nextFromSelectionButton = <Button importance="primary" onClick={moveToNextTab} label="Next" disabled={!hasValidSelectedSources || !hasAnySelectedSources}/>;
                    if (!hasAnySelectedSources) {
                        nextFromSelectionButton = <Tooltip content={"Must select at least one source"}>{nextFromSelectionButton}</Tooltip>;
                    }
                    else if (!hasValidSelectedSources) {
                        nextFromSelectionButton = <Tooltip content={"All selected sources must be named"}>{nextFromSelectionButton}</Tooltip>;
                    }
                    return [closeButton, nextFromSelectionButton];
                case "ScopingConfiguration":
                    let nextFromScopingButton = <Button importance="primary" onClick={moveToNextTab} label="Next" disabled={!allConfiguredSourcesValid}/>;
                    if (!allConfiguredSourcesValid) {
                        nextFromScopingButton = <Tooltip content={"All sources must have required scopes specified"}>{nextFromScopingButton}</Tooltip>;
                    }
                    return [closeButton, <Button importance="secondary" onClick={moveToPreviousTab} label="Back"/>, nextFromScopingButton];
                case "HelmConfiguration":
                    return [closeButton, <Button importance="secondary" onClick={moveToPreviousTab} label="Back"/>, <Button importance="primary" onClick={moveToNextTab} label="Next"/>];
                case "Copy":
                    return [closeButton, <Button importance="secondary" onClick={moveToPreviousTab} label="Back"/>, <Button importance="secondary" onClick={resetToStart} label="Add another application"/>, doneButton];
            }
        }
        else {
            return [closeButton, doneButton];
        }
    }, [allConfiguredSourcesValid, currentTab, hasAnySelectedSources, hasValidSelectedSources, isMultiSourceApp, moveToNextTab, moveToPreviousTab, handleOnClose]);
    return (<Drawer variant={"customActions"} isOpen={open} onClose={handleOnClose} title={"Generate annotations"} width={"wide"} layout={"basic"} customActions={customActions}>
            <GenerateArgoCdAnnotationsDrawerInner ref={innerRef} {...rest} currentTab={currentTab} onSelectedApplicationChanged={handleSelectedApplicationChanged} onSelectedSourcesChanged={handleSelectedSourcesChanged} onConfiguredSourcesChanged={handleConfiguredSourcesChanged}/>
        </Drawer>);
};
